/**
 * The «Адреса» section of a venue card — contract #9 (add), #9.5 (read), #9.6
 * (edit) and #10 (remove), all ADMIN but the read; task T.C4a's "venue
 * dictionary admin", re-voiced by T.Z34 (v1.8.4): the owner runs this section
 * as an ADDRESS-management tool, so it is titled «Адреса» while the api term
 * stays `aliases` (the ADDRESS-kind aliases ARE the addresses; the NAME rows
 * keep living in the same table, marked by the «Тип» column).
 *
 * Four api behaviours shape this component:
 *   * the stored VALUE is the shared normalizer's output, so what the table
 *     shows is the match key the D2 matcher compares against — not necessarily
 *     what the admin typed;
 *   * #9 answers 409 CONFLICT when the normalized value is already mapped to
 *     ANOTHER venue — since v1.8.4 also when it folds onto ANOTHER venue's
 *     MASTER address. That is the one failure a curator can act on, so the form
 *     surfaces it inline (`role="alert"`), never as a toast;
 *   * T.Z34 — the form PRE-CHECKS the typed address against what the client can
 *     already see (this venue's own dictionary + the venues directory) and shows
 *     «этот адрес уже привязан к площадке …» BEFORE the submit, blocking it. The
 *     client fold is a proof-only prefix of the api's, so a hit is real and a
 *     miss costs nothing — the api's 409 stays the authority;
 *   * #30 (v1.8.4) — «Обновить связи» arms one venue-matcher pass (202,
 *     fire-and-forget) so the applications that are UNMATCHED today get another
 *     chance against the addresses this venue now holds. T.Z35c — the offer is
 *     SESSION-scoped: hidden on a fresh card, shown once a write in this pane
 *     session changed the venue's ADDRESSES, hidden again the moment a pass is
 *     armed (the «Пересопоставление запущено…» sentence takes its place). See
 *     `MatchRunPhase` in venues-model for the whole rule, and
 *     `aliasWriteTouchesAddress` for what counts as an address change.
 *
 * The table's source is `useVenueAliases` — the #9.5 read.
 *
 * Wave 6 (owner) reshaped the section's manners: «Добавить» is collapsed behind a
 * «+» (the dictionary is read a hundred times for every time it is written), the
 * row actions appear on hover only, and a row is EDITABLE in place (#9.6, contract
 * v1.5) — a mis-spelled address used to mean delete it and type the whole value
 * again. The stored value is the normalizer's output, so what an edit leaves on
 * screen is the match key, exactly what the add path already shows.
 */

import { useMemo, useState } from 'react';

import type { ApiError } from '../../api/client';
import { useVenueMatchRun } from '../../api/match';
import type { AliasKind } from '../../api/venues';
import { useAddAlias, useRemoveAlias, useUpdateAlias, useVenueAliases, useVenueDirectory } from '../../api/venues';
import { Button, EmptyState, Field, Input, Select } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';

import {
  VENUE_MATCH_RUN_QUEUED_TEXT,
  addressConflictHintText,
  addressKeyHint,
  aliasErrorText,
  aliasKindLabel,
  aliasSourceLabel,
  aliasWriteTouchesAddress,
  findAddressConflict,
  matchRunOfferVisible,
  type MatchRunPhase,
} from './venues-model';

const KIND_OPTIONS: ReadonlyArray<{ value: AliasKind; label: string }> = [
  { value: 'ADDRESS', label: 'Адрес' },
  { value: 'NAME', label: 'Название' },
];

export function VenueAliases({
  venueId,
  venueName,
  venueAddress,
  canEdit,
}: {
  venueId: number;
  /** The card's master name — the pre-check sentence names the holding venue. */
  venueName?: string | null;
  /** The card's master address — a typed twin of it is a duplicate of the venue's own spelling. */
  venueAddress?: string | null;
  canEdit: boolean;
}) {
  const aliases = useVenueAliases(venueId);
  const add = useAddAlias(venueId);
  const remove = useRemoveAlias(venueId);
  const update = useUpdateAlias(venueId);
  const runMatch = useVenueMatchRun();
  // The directory (#6, one 200-row page) is the "other venues" half of the
  // pre-check. It is fetched only while the add form is open on an ADDRESS value —
  // a read the card does not need otherwise, and one a VIEWER never pays for.
  const [kind, setKind] = useState<AliasKind>('ADDRESS');
  const [value, setValue] = useState('');
  // The add form is a disclosure; the row being edited is the one id.
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const directory = useVenueDirectory({
    enabled: canEdit && adding && kind === 'ADDRESS',
  });
  // The #30 answer is a STATE, not a toast: the 202 lands, and the lifecycle below
  // moves the offer out of the way while the queued sentence stands in for it.
  // T.Z35c — the whole lifecycle is THIS pane session's state (see `MatchRunPhase`):
  // `idle` on a fresh card, `changed` once a write touched the addresses, `queued`
  // once the pass is armed. The card remounts the section per venue
  // (`key={venueId}`), so opening another venue starts over at `idle` — a reload
  // forgets it too, and the worker's queue is the record of what was armed.
  const [matchPhase, setMatchPhase] = useState<MatchRunPhase>('idle');

  const error = aliasErrorText(add.isError ? (add.error as ApiError | null) : null, kind);
  // The pre-check: one folded key against the aliases and master addresses the
  // client already holds. `directory.data` is undefined until the read lands —
  // the check then simply covers less, and the api's 409 covers the rest.
  const conflict = useMemo(
    () =>
      adding
        ? findAddressConflict({
            kind,
            value,
            ownVenueId: venueId,
            ownVenueName: venueName,
            ownVenueAddress: venueAddress,
            ownAliases: aliases,
            directory: directory.data,
          })
        : null,
    [adding, kind, value, venueId, venueName, venueAddress, aliases, directory.data],
  );
  // An ADDRESS value the client can already place is blocked before the submit —
  // the api would answer 200 (same venue) or 409 (another venue), and neither is
  // an edit the user meant to make.
  const duplicate = conflict !== null && addressKeyHint(value.trim()) !== '';
  // The address rows the venue holds RIGHT NOW — a pass without one cannot resolve
  // anything the address tiers look at, so an offer is pointless while this is empty.
  const hasAddressAlias = aliases.some((alias) => alias.kind === 'ADDRESS');

  /**
   * A successful alias write moves the offer to `changed` — but only when the write
   * actually touched the ADDRESSES (T.Z35c: a NAME add or a NAME→NAME edit arms
   * nothing). Called from the three write paths below, each of which knows the kinds
   * its own move involves.
   */
  const noteAddressWrite = (op: 'add' | 'edit', after: AliasKind, before?: AliasKind) => {
    if (aliasWriteTouchesAddress(op, after, before)) setMatchPhase('changed');
  };

  const submit = () => {
    const text = value.trim();
    if (!text || duplicate) return;
    add.mutate(
      { kind, value: text },
      {
        onSuccess: () => {
          setValue('');
          setAdding(false);
          // The 409 of a clash leaves the form as it is; a success clears the
          // stale error along with the input.
          add.reset();
          noteAddressWrite('add', kind);
        },
      },
    );
  };

  return (
    <section className="card venue-section" data-testid="venue-aliases">
      <header className="venue-section__head">
        <h2 className="venue-section__title">Адреса</h2>
        <span className="venue-section__hint">
          Адреса и названия, по которым снапшоты находят эту площадку. Адрес должен быть уникальным по всей системе.
        </span>
      </header>

      <div className="venue-section__body">
        {aliases.length === 0 ? (
          <EmptyState
            title="Адресов пока нет"
            hint={
              canEdit
                ? 'Добавьте адрес или название — сопоставитель найдёт по ним заявления, у которых площадки нет.'
                : 'Адресами площадок управляет администратор.'
            }
          />
        ) : (
          <TableWrap>
            <table className="data" data-testid="alias-table">
              <thead>
                <tr>
                  <th>Тип</th>
                  <th>Значение</th>
                  <th>Источник</th>
                  {canEdit ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {aliases.map((alias) =>
                  canEdit && editingId === alias.id ? (
                    <tr key={alias.id} data-testid="alias-edit-row" data-alias-id={alias.id}>
                      <td data-testid="alias-kind-edit">
                        <Select
                          data-testid="alias-kind-select"
                          value={alias.kind}
                          onChange={(event) => {
                            const nextKind = event.target.value as AliasKind;
                            update.mutate(
                              { aliasId: alias.id, patch: { kind: nextKind } },
                              {
                                onSuccess: () => {
                                  setEditingId(null);
                                  // A kind flip moves the row into or out of the
                                  // address dictionary — both are an address change.
                                  noteAddressWrite('edit', nextKind, alias.kind);
                                },
                              },
                            );
                          }}
                        >
                          {KIND_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td colSpan={2} data-testid="alias-value-edit">
                        <AliasValueEditor
                          alias={alias}
                          pending={update.isPending && update.variables?.aliasId === alias.id}
                          onDone={(patch) =>
                            update.mutate(
                              { aliasId: alias.id, patch },
                              {
                                onSuccess: () => {
                                  update.reset();
                                  setEditingId(null);
                                  // A value rewrite keeps the row's kind, so the move
                                  // is an address change exactly when the row is one.
                                  noteAddressWrite('edit', alias.kind, alias.kind);
                                },
                              },
                            )
                          }
                          onCancel={() => {
                            update.reset();
                            setEditingId(null);
                          }}
                        />
                      </td>
                      <td />
                    </tr>
                  ) : (
                    <tr key={alias.id} data-testid="alias-row" data-alias-id={alias.id}>
                      <td data-testid="alias-kind">{aliasKindLabel(alias.kind)}</td>
                      <td className="alias-value" data-testid="alias-value">
                        {alias.value}
                      </td>
                      <td className="cell-muted" data-testid="alias-source">
                        {aliasSourceLabel(alias.source)}
                      </td>
                      {canEdit ? (
                        <td className="row-actions">
                          <Button
                            size="sm"
                            data-testid="alias-edit"
                            onClick={() => {
                              update.reset();
                              setEditingId(alias.id);
                            }}
                          >
                            Изменить
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            loading={remove.isPending && remove.variables === alias.id}
                            aria-label={`Удалить адрес ${alias.value}`}
                            data-testid="alias-delete"
                            onClick={() => remove.mutate(alias.id)}
                          >
                            Удалить
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </TableWrap>
        )}

        {remove.isError ? (
          <p className="field__error" role="alert" data-testid="alias-delete-error">
            {aliasErrorText(remove.error as ApiError | null) ?? 'Не удалось удалить адрес.'}
          </p>
        ) : null}

        {update.isError ? (
          <p className="field__error" role="alert" data-testid="alias-edit-error">
            {aliasErrorText(update.error as ApiError | null, aliases.find((row) => row.id === update.variables?.aliasId)?.kind) ??
              'Не удалось сохранить адрес.'}
          </p>
        ) : null}

        {canEdit ? (
          adding ? (
          <form
            className="inline-form"
            data-testid="alias-form"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <Field label="Тип" htmlFor="alias-kind">
              <Select
                id="alias-kind"
                data-testid="alias-kind-select"
                value={kind}
                onChange={(event) => setKind(event.target.value as AliasKind)}
              >
                {KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Значение"
              htmlFor="alias-value"
              hint="Сохраняется в нормализованном виде — как его увидит сопоставитель"
              error={conflict ? addressConflictHintText(conflict) : (error ?? undefined)}
            >
              <Input
                id="alias-value"
                data-testid="alias-value-input"
                value={value}
                aria-invalid={duplicate}
                placeholder={kind === 'ADDRESS' ? '115569, ул.Шипиловский, д.37 к.2, Москва' : 'Шипиловская площадка'}
                onChange={(event) => setValue(event.target.value)}
              />
            </Field>
            <Button
              variant="primary"
              size="sm"
              loading={add.isPending}
              disabled={!value.trim() || duplicate}
              data-testid="alias-submit"
              onClick={submit}
            >
              Добавить
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="alias-add-cancel"
              onClick={() => {
                add.reset();
                setValue('');
                setAdding(false);
              }}
            >
              Отмена
            </Button>
          </form>
          ) : (
            <div className="section-add">
              <Button
                size="sm"
                variant="secondary"
                data-testid="alias-add-toggle"
                aria-expanded={adding}
                onClick={() => setAdding(true)}
              >
                + Добавить адрес
              </Button>
            </div>
          )
        ) : null}

        {/* T.Z35c — the offer exists only inside the session that earned it: a fresh card
            renders nothing here, a changed address renders the button + hint, an armed
            pass renders the queued sentence in their place. `hasAddressAlias` stays the
            second gate on the BUTTON (the api's pass walks the address tiers, so an offer
            on a venue with no address row would be noise — e.g. right after an
            ADDRESS→NAME flip removed the last one). */}
        {canEdit && matchPhase !== 'idle' ? (
          <div className="section-add" data-testid="venue-match-run">
            {matchRunOfferVisible(matchPhase) && hasAddressAlias ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={runMatch.isPending}
                  data-testid="venue-match-run-button"
                  onClick={() =>
                    runMatch.mutate(undefined, {
                      onSuccess: () => {
                        runMatch.reset();
                        // The pass is in the worker's hands now: the offer steps
                        // aside and the queued sentence stands in for it.
                        setMatchPhase('queued');
                      },
                      // A refused ask is not an armed pass — the offer stays up
                      // (with the error below it) for the retry.
                    })
                  }
                >
                  Обновить связи
                </Button>
                <span className="venue-edit__audit-hint" data-testid="venue-match-run-hint">
                  Повторить сопоставление: заявления без площадки проверят ещё раз по этим адресам
                </span>
              </>
            ) : null}
            {matchPhase === 'queued' ? (
              <p className="field__hint" role="status" data-testid="venue-match-run-status">
                {VENUE_MATCH_RUN_QUEUED_TEXT}
              </p>
            ) : null}
            {runMatch.isError ? (
              <p className="field__error" role="alert" data-testid="venue-match-run-error">
                Не удалось запустить пересопоставление. Повторите попытку позже.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The value half of an alias row's inline edit (#9.6). The api normalizes whatever
 * is typed, so the editor does not try to pre-normalize — it only refuses an empty
 * field and hands the raw spelling to the api, the same contract the add form runs on.
 */
function AliasValueEditor({
  alias,
  pending,
  onDone,
  onCancel,
}: {
  alias: { value: string };
  pending: boolean;
  onDone: (patch: { value: string }) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(alias.value);
  const dirty = text.trim() !== '' && text.trim() !== alias.value;

  return (
    <div className="alias-editor">
      <Input
        data-testid="alias-value-input"
        aria-label="Значение адреса"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (dirty) onDone({ value: text.trim() });
          }
        }}
      />
      <Button
        variant="primary"
        size="sm"
        loading={pending}
        disabled={!dirty}
        data-testid="alias-edit-save"
        onClick={() => onDone({ value: text.trim() })}
      >
        Сохранить
      </Button>
      <Button size="sm" variant="ghost" data-testid="alias-edit-cancel" onClick={onCancel}>
        Отмена
      </Button>
      <span className="venue-edit__audit-hint">Сохраняется в нормализованном виде</span>
    </div>
  );
}
